import { useState, useEffect } from 'react';
import {
  Dialog, IconButton,
  InputAdornment,
  List as MuiList, ListItem, ListItemButton,
  ListItemIcon, ListItemText, TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser, useCurrentTeamId } from 'lib/contexts/ApplicationState';
import { ContentList, ContentListsItemType } from 'lib/types';
import Link from 'next/link';
import { QuickAddProject } from 'components/AddEditProject/AddEditProject';
import { pluralize } from 'lib/helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface Props {
  itemIds: number[];
  itemType: ContentListsItemType;
  onClose: () => void;
  context: 'project' | 'list';
}

export const BulkSaveToProjectListModal = ({
  itemIds, itemType, onClose, context,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const currentTeamId = useCurrentTeamId();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isSendingData, setIsSendingData] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    data: lists, isLoading: isLoadingLists,
    isPending: isPendingLists, error: listsError,
  } = useQuery({
    queryKey: ['getProjectLists', currentTeamId, currentUser?.id],
    queryFn: () => API.getProjectLists(),
    enabled: !!currentUser,
  });

  useEffect(() => {
    if (listsError) {
      enqueueSnackbar(`Error loading project lists: ${listsError}`, {
        variant: 'error',
      });
    }
  }, [listsError, enqueueSnackbar]);

  const addToList = async (list: ContentList) => {
    try {
      setIsSendingData(true);
      await API.bulkAddItemsToList(list.id, itemIds, itemType);

      // Invalidate getProjectsForPost queries for all posts that were added
      if (itemType === 'Post') {
        itemIds.forEach((postId) => {
          queryClient.invalidateQueries({
            queryKey: ['getProjectsForPost', postId],
          });
        });
      }

      setIsSendingData(false);
      onClose();
      enqueueSnackbar(`Added to ${list.name}!`, {
        variant: 'success',
        autoHideDuration: 9000,
        action: (
          <Link href={`/projects/${list.publicProjectId}`}>
            View project
          </Link>
        ),
      });
    } catch (error) {
      enqueueSnackbar(`Error adding ${itemType} to list: ${error}`, {
        variant: 'error',
      });
    }
  };

  const save = async (list: ContentList) => {
    if (!list.id) return;
    addToList(list);
  };

  const handleQuickAddProjectSave = () => {
    // Invalidate getProjectsForPost queries for all posts that were added to the new project
    if (itemType === 'Post') {
      itemIds.forEach((postId) => {
        queryClient.invalidateQueries({
          queryKey: ['getProjectsForPost', postId],
        });
      });
    }
    onClose();
  };

  const filterItemsBySearch = (items: any[], searchText: string) => {
    if (!searchText) return items;
    const query = searchText.toLowerCase();
    return items.filter((item) => {
      if (item.name) return item.name.toLowerCase().includes(query);
      if (item.teamName) return item.teamName.toLowerCase().includes(query);
      return false;
    });
  };

  const [searchQuery, setSearchQuery] = useState('');
  const filteredListsBySearch = filterItemsBySearch(
    lists || [], searchQuery,
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <div className="absolute right-2 top-2 text-orange">
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="p-4">
        <>
          {open ? (
            <QuickAddProject
              onCancel={() => setOpen(false)}
              onSave={handleQuickAddProjectSave}
              project={{
                postIds: itemIds,
              }}
            />
          )
            : (
              <>
                <div className="flex flex-wrap sm:flex-nowrap items-center">
                  <h1 className="text-lg text-orange font-bold mr-5">
                    Save
                    {' '}
                    {itemIds.length}
                    {' '}
                    {pluralize(itemType, itemIds.length)}
                    {' '}
                    to a
                    {' '}
                    {context}
                  </h1>
                </div>
                <div>
                  <div className="p-2 sticky top-0 bg-white z-10">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search projects"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery('');
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <MuiList>
                    {currentTeamId && !lists?.length && !isLoadingLists && !isPendingLists && (
                      <div className="text-center p-2">
                        <p className="mb-2">
                          No projects found.
                        </p>
                        <Link className="btn-primary" href="/projects/new">
                          Create a project
                        </Link>
                      </div>
                    )}
                    {(isLoadingLists || isPendingLists) && (
                      <div className="text-center p-2">
                        Loading your projects...
                      </div>
                    )}
                    {filteredListsBySearch.map((l) => (
                      <ListItem key={l.id} disablePadding>
                        <ListItemButton
                          disabled={isSendingData}
                          onClick={() => save(l)}
                        >
                          <ListItemIcon>
                            {(l.contentListItemId) ? <CheckIcon color="success" /> : <AddIcon color="inherit" />}
                          </ListItemIcon>
                          <ListItemText>
                            <div>
                              {l.name}
                              {l.teamName && (
                                <span className="font-light text-xs ml-2">
                                  (
                                  {l.teamName}
                                  )
                                </span>
                              )}
                            </div>
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </MuiList>
                </div>
                <div>
                  <button type="button" className="py-4 w-full hover:bg-hintgray" onClick={() => setOpen(true)}>
                    <AddIcon />
                    {' '}
                    Create a New Project
                  </button>
                </div>
              </>
            )}
        </>
      </div>
    </Dialog>
  );
};
